import { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import { useOnboardingStore } from './stores/onboardingStore'
import { useSession } from './hooks/useSession'
import { AppLayout } from './layouts/AppLayout'
import { PermissionRoute } from './components/PermissionGuard'
import { ErrorBoundary } from './components/ErrorBoundary'
import { CookieBanner } from './components/CookieBanner'
import { WelcomeModal } from './components/WelcomeModal'
import { OnboardingTour } from './components/OnboardingTour'
import { PERMISSIONS } from './lib/permissions'

// Eagerly loaded (critical pages)
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { PatientsPage } from './pages/PatientsPage'

// Lazy loaded pages
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const PatientDetailPage = lazy(() => import('./pages/PatientDetailPage').then(m => ({ default: m.PatientDetailPage })))
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage').then(m => ({ default: m.AppointmentsPage })))
const TreatmentsPage = lazy(() => import('./pages/TreatmentsPage').then(m => ({ default: m.TreatmentsPage })))
const InventoryPage = lazy(() => import('./pages/InventoryPage').then(m => ({ default: m.InventoryPage })))
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })))
const ClinicsPage = lazy(() => import('./pages/ClinicsPage').then(m => ({ default: m.ClinicsPage })))
const WaitlistPage = lazy(() => import('./pages/WaitlistPage').then(m => ({ default: m.WaitlistPage })))
const OdontogramPage = lazy(() => import('./pages/OdontogramPage').then(m => ({ default: m.OdontogramPage })))
const BudgetsPage = lazy(() => import('./pages/BudgetsPage').then(m => ({ default: m.BudgetsPage })))
const ProcedureCatalogPage = lazy(() => import('./pages/ProcedureCatalogPage').then(m => ({ default: m.ProcedureCatalogPage })))
const InsurancePage = lazy(() => import('./pages/InsurancePage').then(m => ({ default: m.InsurancePage })))
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })))
const PermissionsPage = lazy(() => import('./pages/PermissionsPage').then(m => ({ default: m.PermissionsPage })))
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const AuditPage = lazy(() => import('./pages/AuditPage').then(m => ({ default: m.AuditPage })))
const RemindersPage = lazy(() => import('./pages/RemindersPage').then(m => ({ default: m.RemindersPage })))
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const EvolutionsPage = lazy(() => import('./pages/EvolutionsPage').then(m => ({ default: m.EvolutionsPage })))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })))
const AutomationsPage = lazy(() => import('./pages/AutomationsPage').then(m => ({ default: m.AutomationsPage })))
const AgentsPage = lazy(() => import('./pages/AgentsPage').then(m => ({ default: m.AgentsPage })))
const PortalPage = lazy(() => import('./pages/PortalPage').then(m => ({ default: m.PortalPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const LegalPage = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.LegalPage })))
const CookiesPage = lazy(() => import('./pages/CookiesPage').then(m => ({ default: m.CookiesPage })))

// Fase 1: Consentimientos + Laboratorio
const ConsentsPage = lazy(() => import('./pages/ConsentsPage').then(m => ({ default: m.ConsentsPage })))
const ConsentTemplatesPage = lazy(() => import('./pages/ConsentTemplatesPage').then(m => ({ default: m.ConsentTemplatesPage })))
const ConsentSignPage = lazy(() => import('./pages/ConsentSignPage').then(m => ({ default: m.ConsentSignPage })))
const LabPage = lazy(() => import('./pages/LabPage').then(m => ({ default: m.LabPage })))
const LabOrdersPage = lazy(() => import('./pages/LabOrdersPage').then(m => ({ default: m.LabOrdersPage })))
const LabOrderDetailPage = lazy(() => import('./pages/LabOrderDetailPage').then(m => ({ default: m.LabOrderDetailPage })))
const LabSuppliersPage = lazy(() => import('./pages/LabSuppliersPage').then(m => ({ default: m.LabSuppliersPage })))
const LabSupplierDetailPage = lazy(() => import('./pages/LabSupplierDetailPage').then(m => ({ default: m.LabSupplierDetailPage })))

// Fase 2: Contabilidad + Recursos
const AccountingPage = lazy(() => import('./pages/AccountingPage').then(m => ({ default: m.AccountingPage })))
const ExpensesPage = lazy(() => import('./pages/ExpensesPage').then(m => ({ default: m.ExpensesPage })))
const ResourcesPage = lazy(() => import('./pages/ResourcesPage').then(m => ({ default: m.ResourcesPage })))
const ResourceCalendarPage = lazy(() => import('./pages/ResourceCalendarPage').then(m => ({ default: m.ResourceCalendarPage })))
const ProfitLossPage = lazy(() => import('./pages/ProfitLossPage').then(m => ({ default: m.ProfitLossPage })))
const ExpenseCategoriesPage = lazy(() => import('./pages/ExpenseCategoriesPage').then(m => ({ default: m.ExpenseCategoriesPage })))
const ResourceEquipmentPage = lazy(() => import('./pages/ResourceEquipmentPage').then(m => ({ default: m.ResourceEquipmentPage })))

// Fase 3: Sala de espera + Marketing
const CheckInPage = lazy(() => import('./pages/CheckInPage').then(m => ({ default: m.CheckInPage })))
const WaitingDisplayPage = lazy(() => import('./pages/WaitingDisplayPage').then(m => ({ default: m.WaitingDisplayPage })))
const MarketingPage = lazy(() => import('./pages/MarketingPage').then(m => ({ default: m.MarketingPage })))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then(m => ({ default: m.CampaignsPage })))
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })))
const SegmentsPage = lazy(() => import('./pages/SegmentsPage').then(m => ({ default: m.SegmentsPage })))

// Fase 4: Reputacion + KPIs + Proveedores
const ReputationPage = lazy(() => import('./pages/ReputationPage').then(m => ({ default: m.ReputationPage })))
const ReviewsPage = lazy(() => import('./pages/ReviewsPage').then(m => ({ default: m.ReviewsPage })))
const ReviewSolicitationPage = lazy(() => import('./pages/ReviewSolicitationPage').then(m => ({ default: m.ReviewSolicitationPage })))
const ClinicalKpiPage = lazy(() => import('./pages/ClinicalKpiPage').then(m => ({ default: m.ClinicalKpiPage })))
const ClinicalKpiDoctorsPage = lazy(() => import('./pages/ClinicalKpiDoctorsPage').then(m => ({ default: m.ClinicalKpiDoctorsPage })))
const ClinicalKpiTreatmentsPage = lazy(() => import('./pages/ClinicalKpiTreatmentsPage').then(m => ({ default: m.ClinicalKpiTreatmentsPage })))
const ClinicalKpiDoctorDetailPage = lazy(() => import('./pages/ClinicalKpiDoctorDetailPage').then(m => ({ default: m.ClinicalKpiDoctorDetailPage })))
const RecallPage = lazy(() => import('./pages/RecallPage').then(m => ({ default: m.RecallPage })))
const SuppliersPage = lazy(() => import('./pages/SuppliersPage').then(m => ({ default: m.SuppliersPage })))
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage').then(m => ({ default: m.PurchaseOrdersPage })))
const PurchaseOrderDetailPage = lazy(() => import('./pages/PurchaseOrderDetailPage').then(m => ({ default: m.PurchaseOrderDetailPage })))

// Tutoriales y Centro de Ayuda
const TutorialsPage = lazy(() => import('./tutorials/pages/TutorialsPage').then(m => ({ default: m.TutorialsPage })))
const TutorialDetailPage = lazy(() => import('./tutorials/pages/TutorialDetailPage').then(m => ({ default: m.TutorialDetailPage })))

// Onboarding
const GettingStartedPage = lazy(() => import('./pages/GettingStartedPage').then(m => ({ default: m.GettingStartedPage })))

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function AppContent() {
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAuthStore()
  const { progress, loadProgress, isOnboardingComplete } = useOnboardingStore()
  const [showWelcome, setShowWelcome] = useState(false)
  const [tourRun, setTourRun] = useState(false)
  const isDemoUser = user?.isDemo === true

  useSession()

  // Cargar progreso de onboarding al autenticar
  useEffect(() => {
    if (isAuthenticated) {
      loadProgress()
    }
  }, [isAuthenticated, loadProgress])

  // Mostrar welcome modal cuando el progreso se carga y no ha visto la bienvenida
  // (solo para usuarios no-demo; los usuarios demo van directo a la app)
  useEffect(() => {
    if (isAuthenticated && !isDemoUser && progress && !progress.hasSeenWelcome) {
      const timer = setTimeout(() => setShowWelcome(true), 500)
      return () => clearTimeout(timer)
    }
  }, [isAuthenticated, isDemoUser, progress])

  // Redirección a getting-started si el onboarding no está completo (solo en rutas protegidas)
  useEffect(() => {
    if (
      isAuthenticated &&
      progress &&
      progress.hasSeenWelcome &&
      !isOnboardingComplete() &&
      location.pathname === '/'
    ) {
      // No redirigimos automáticamente para no ser intrusivos;
      // el QuickStartChecklist se muestra inline en el dashboard
    }
  }, [isAuthenticated, progress, isOnboardingComplete, location.pathname])

  // Escuchar bloqueos de operaciones en modo demo read-only
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      console.warn('[ReadOnly] Operación bloqueada:', detail?.method, detail?.url)
      toast.error('Modo demo de solo lectura. No se pueden modificar datos.', {
        duration: 3000,
        icon: '🛡️',
      })
    }
    window.addEventListener('readonly-blocked', handler)
    return () => window.removeEventListener('readonly-blocked', handler)
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            padding: '14px 18px',
            fontSize: '13px',
          },
        }}
      />
      <CookieBanner />

      {/* Welcome Modal */}
      {showWelcome && (
        <WelcomeModal
          onStartTour={() => {
            setShowWelcome(false)
            setTourRun(true)
          }}
          onClose={() => setShowWelcome(false)}
        />
      )}

      {/* Onboarding Tour */}
      {tourRun && (
        <OnboardingTour
          run={tourRun}
          onComplete={() => setTourRun(false)}
          onSkip={() => setTourRun(false)}
        />
      )}

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/privacidad" element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        } />
        <Route path="/aviso-legal" element={
          <Suspense fallback={<PageLoader />}>
            <LegalPage />
          </Suspense>
        } />
        <Route path="/cookies" element={
          <Suspense fallback={<PageLoader />}>
            <CookiesPage />
          </Suspense>
        } />
        <Route path="/portal/:token" element={
          <Suspense fallback={<PageLoader />}>
            <PortalPage />
          </Suspense>
        } />
        {/* Public routes - no auth required */}
        <Route path="/consents/:id/sign" element={
          <Suspense fallback={<PageLoader />}>
            <ConsentSignPage />
          </Suspense>
        } />
        <Route path="/reception/display" element={
          <Suspense fallback={<PageLoader />}>
            <WaitingDisplayPage />
          </Suspense>
        } />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/getting-started" element={<GettingStartedPage />} />
                    <Route path="/patients" element={<PermissionRoute permissions={['patients:read', 'patients:manage']}><PatientsPage /></PermissionRoute>} />
                    <Route path="/patients/:id" element={<PermissionRoute permissions={['patients:read', 'patients:manage']}><PatientDetailPage /></PermissionRoute>} />
                    <Route path="/patients/:id/evolutions" element={<PermissionRoute permissions={['evolutions:read', 'evolutions:manage']}><EvolutionsPage /></PermissionRoute>} />
                    <Route path="/patients/:id/documents" element={<PermissionRoute permissions={['patients:read', 'patients:manage']}><DocumentsPage /></PermissionRoute>} />
                    <Route path="/appointments" element={<PermissionRoute permissions={['appointments:read', 'appointments:manage']}><AppointmentsPage /></PermissionRoute>} />
                    <Route path="/treatments" element={<PermissionRoute permissions={['treatments:read', 'treatments:manage']}><TreatmentsPage /></PermissionRoute>} />
                    <Route path="/inventory" element={<PermissionRoute permissions={['inventory:read', 'inventory:manage']}><InventoryPage /></PermissionRoute>} />
                    <Route path="/billing" element={<PermissionRoute permissions={['billing:read', 'billing:manage']}><BillingPage /></PermissionRoute>} />
                    <Route path="/clinics" element={<PermissionRoute permissions={['clinics:read', 'clinics:manage']}><ClinicsPage /></PermissionRoute>} />
                    <Route path="/waitlist" element={<PermissionRoute permissions={['waitlist:read', 'waitlist:manage']}><WaitlistPage /></PermissionRoute>} />
                    <Route path="/patients/:id/odontogram" element={<PermissionRoute permissions={['odontogram:read', 'odontogram:manage']}><OdontogramPage /></PermissionRoute>} />
                    <Route path="/patients/recall" element={<PermissionRoute permissions={['patients:read', 'patients:manage']}><RecallPage /></PermissionRoute>} />
                    <Route path="/odontogram" element={<PermissionRoute permissions={['odontogram:read', 'odontogram:manage']}><OdontogramPage /></PermissionRoute>} />
                    <Route path="/budgets" element={<PermissionRoute permissions={['budgets:read', 'budgets:manage']}><BudgetsPage /></PermissionRoute>} />
                    <Route path="/procedure-catalog" element={<PermissionRoute permissions={['budgets:read', 'budgets:manage']}><ProcedureCatalogPage /></PermissionRoute>} />
                    <Route path="/insurance" element={<PermissionRoute permissions={['insurance:read', 'insurance:manage']}><InsurancePage /></PermissionRoute>} />
                    <Route path="/users" element={<PermissionRoute permissions={['users:read', 'users:manage']}><UsersPage /></PermissionRoute>} />
                    <Route path="/permissions" element={<PermissionRoute permissions={['users:manage']}><PermissionsPage /></PermissionRoute>} />
                    <Route path="/reports" element={<PermissionRoute permissions={['analytics:read', 'analytics:manage']}><ReportsPage /></PermissionRoute>} />
                    <Route path="/audit" element={<PermissionRoute permissions={['users:manage']}><AuditPage /></PermissionRoute>} />
                    <Route path="/reminders" element={<PermissionRoute permissions={['reminders:read', 'reminders:manage']}><RemindersPage /></PermissionRoute>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
<Route path="/automations" element={<PermissionRoute permissions={['users:manage']}><AutomationsPage /></PermissionRoute>} />
<Route path="/agents" element={<PermissionRoute permissions={['users:manage']}><AgentsPage /></PermissionRoute>} />

                      {/* Fase 1: Consentimientos + Laboratorio */}
                      <Route path="/consents" element={<PermissionRoute permissions={[PERMISSIONS.CONSENTS_READ, PERMISSIONS.CONSENTS_MANAGE]}><ConsentsPage /></PermissionRoute>} />
                      <Route path="/consents/templates" element={<PermissionRoute permissions={[PERMISSIONS.CONSENTS_READ, PERMISSIONS.CONSENTS_MANAGE]}><ConsentTemplatesPage /></PermissionRoute>} />
                      <Route path="/lab" element={<PermissionRoute permissions={[PERMISSIONS.LAB_READ, PERMISSIONS.LAB_MANAGE]}><LabPage /></PermissionRoute>} />
                      <Route path="/lab/orders" element={<PermissionRoute permissions={[PERMISSIONS.LAB_READ, PERMISSIONS.LAB_MANAGE]}><LabOrdersPage /></PermissionRoute>} />
                      <Route path="/lab/orders/:id" element={<PermissionRoute permissions={[PERMISSIONS.LAB_READ, PERMISSIONS.LAB_MANAGE]}><LabOrderDetailPage /></PermissionRoute>} />
                      <Route path="/lab/suppliers" element={<PermissionRoute permissions={[PERMISSIONS.LAB_READ, PERMISSIONS.LAB_MANAGE]}><LabSuppliersPage /></PermissionRoute>} />
<Route path="/lab/suppliers/:id" element={<PermissionRoute permissions={[PERMISSIONS.LAB_READ, PERMISSIONS.LAB_MANAGE]}><LabSupplierDetailPage /></PermissionRoute>} />

                      {/* Fase 2: Contabilidad + Recursos */}
                      <Route path="/accounting" element={<PermissionRoute permissions={[PERMISSIONS.ACCOUNTING_READ, PERMISSIONS.ACCOUNTING_MANAGE]}><AccountingPage /></PermissionRoute>} />
                      <Route path="/accounting/expenses" element={<PermissionRoute permissions={[PERMISSIONS.ACCOUNTING_READ, PERMISSIONS.ACCOUNTING_MANAGE]}><ExpensesPage /></PermissionRoute>} />
                      <Route path="/accounting/profit-loss" element={<PermissionRoute permissions={[PERMISSIONS.ACCOUNTING_READ, PERMISSIONS.ACCOUNTING_MANAGE]}><ProfitLossPage /></PermissionRoute>} />
                      <Route path="/accounting/categories" element={<PermissionRoute permissions={[PERMISSIONS.ACCOUNTING_READ, PERMISSIONS.ACCOUNTING_MANAGE]}><ExpenseCategoriesPage /></PermissionRoute>} />
                      <Route path="/resources" element={<PermissionRoute permissions={[PERMISSIONS.RESOURCES_READ, PERMISSIONS.RESOURCES_MANAGE]}><ResourcesPage /></PermissionRoute>} />
                      <Route path="/resources/calendar" element={<PermissionRoute permissions={[PERMISSIONS.RESOURCES_READ, PERMISSIONS.RESOURCES_MANAGE]}><ResourceCalendarPage /></PermissionRoute>} />
                      <Route path="/resources/equipment" element={<PermissionRoute permissions={[PERMISSIONS.RESOURCES_READ, PERMISSIONS.RESOURCES_MANAGE]}><ResourceEquipmentPage /></PermissionRoute>} />

                      {/* Fase 3: Sala de espera + Marketing */}
                      <Route path="/reception/checkin" element={<PermissionRoute permissions={[PERMISSIONS.RECEPTION_MANAGE]}><CheckInPage /></PermissionRoute>} />
                      <Route path="/reception/display" element={<WaitingDisplayPage />} />
                      <Route path="/marketing" element={<PermissionRoute permissions={[PERMISSIONS.MARKETING_READ, PERMISSIONS.MARKETING_MANAGE]}><MarketingPage /></PermissionRoute>} />
                      <Route path="/marketing/campaigns" element={<PermissionRoute permissions={[PERMISSIONS.MARKETING_READ, PERMISSIONS.MARKETING_MANAGE]}><CampaignsPage /></PermissionRoute>} />
                      <Route path="/marketing/campaigns/:id" element={<PermissionRoute permissions={[PERMISSIONS.MARKETING_READ, PERMISSIONS.MARKETING_MANAGE]}><CampaignDetailPage /></PermissionRoute>} />
                      <Route path="/marketing/segments" element={<PermissionRoute permissions={[PERMISSIONS.MARKETING_READ, PERMISSIONS.MARKETING_MANAGE]}><SegmentsPage /></PermissionRoute>} />

                      {/* Fase 4: Reputacion + KPIs + Proveedores */}
                      <Route path="/reputation" element={<PermissionRoute permissions={[PERMISSIONS.REPUTATION_READ, PERMISSIONS.REPUTATION_MANAGE]}><ReputationPage /></PermissionRoute>} />
                      <Route path="/reputation/reviews" element={<PermissionRoute permissions={[PERMISSIONS.REPUTATION_READ, PERMISSIONS.REPUTATION_MANAGE]}><ReviewsPage /></PermissionRoute>} />
                      <Route path="/reputation/solicitations" element={<PermissionRoute permissions={[PERMISSIONS.REPUTATION_READ, PERMISSIONS.REPUTATION_MANAGE]}><ReviewSolicitationPage /></PermissionRoute>} />
                      <Route path="/clinical-kpi" element={<PermissionRoute permissions={[PERMISSIONS.CLINICAL_KPIS_READ]}><ClinicalKpiPage /></PermissionRoute>} />
                      <Route path="/clinical-kpi/doctors" element={<PermissionRoute permissions={[PERMISSIONS.CLINICAL_KPIS_READ]}><ClinicalKpiDoctorsPage /></PermissionRoute>} />
                      <Route path="/clinical-kpi/doctors/:id" element={<PermissionRoute permissions={[PERMISSIONS.CLINICAL_KPIS_READ]}><ClinicalKpiDoctorDetailPage /></PermissionRoute>} />
                      <Route path="/clinical-kpi/treatments" element={<PermissionRoute permissions={[PERMISSIONS.CLINICAL_KPIS_READ]}><ClinicalKpiTreatmentsPage /></PermissionRoute>} />
                      <Route path="/suppliers" element={<PermissionRoute permissions={[PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_MANAGE]}><SuppliersPage /></PermissionRoute>} />
                      <Route path="/purchase-orders" element={<PermissionRoute permissions={[PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_MANAGE]}><PurchaseOrdersPage /></PermissionRoute>} />
                      <Route path="/purchase-orders/:id" element={<PermissionRoute permissions={[PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_MANAGE]}><PurchaseOrderDetailPage /></PermissionRoute>} />

                      {/* Tutoriales y Centro de Ayuda */}
                      <Route path="/tutorials" element={<TutorialsPage />} />
                      <Route path="/tutorials/:slug" element={<TutorialDetailPage />} />
                  </Routes>
                </Suspense>
                </ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  )
}

export default AppContent
